import React, { useCallback, useEffect } from 'react'
import { Form, Formik } from 'formik'
import { isEmpty } from 'lodash'
import { Grid } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { PlanType } from '@labrav/react-components'
import {
  isNonEmptyNumber,
  ScrollToFieldError,
  useAccountId,
} from '@labrav/common'

import { useStyles } from './RenewChannelOfferSubscription.styles'
import { useDispatch, useSelector } from '../../../store'
import type { RootState } from '../../../store/types'
import { useTimeZone } from '../../../common/utils/hooks/useTimeZone'
import { useSyncFlyoutRouteParams } from '../../../common/utils/hooks/useSyncFlyoutRouteParams'
import {
  clearActiveReceivedResaleAuthorization,
  getActiveReceivedResaleAuthorizations,
} from '../../../modules/subscription/subscriptionsListDeps'
import {
  getCurrentSubscriptionDetail,
  getInProgressCPPOAgreementBasedOffer,
} from '../../../modules/subscription/action'
import type { ChannelPartnerPrivateOfferInCreation } from '../../../types/flyout'
import type { DiscountType } from '../../../types/flyout'
import type { SubscriptionDetailObject } from '../../../modules/subscription/reducer'
import type { RenewChannelOfferResellerAuthorization } from './renewChannelOfferTypes'
import { OfferSubscriptionDetail } from './OfferSubscriptionDetail/OfferSubscriptionDetail'
import { AddEditRenewChannelOfferForm } from './AddEditRenewChannelOfferForm/AddEditRenewChannelOfferForm'
import { AddEditChannelOfferHeader } from '../../ChannelOffers/AddEditChannelOfferComponents/AddEditChannelOfferHeader/AddEditChannelOfferHeader'
import { AuthorizationDetails } from '../../ChannelOffers/AddEditChannelOfferComponents/AuthorizationDetails/AuthorizationDetails'
import { AddEditChannelOfferFooter } from '../../ChannelOffers/AddEditChannelOffer/AddEditChannelOfferFooter/AddEditChannelOfferFooter'
import { generateABOChannelOfferValidationSchema } from '../../../common/utils/ChannelPartnerPrivateOfferValidation'
import { sendChannelOffer } from '../../../modules/channelPartnerPrivateOffer/action'
import type { ReceivedResellerAuthorization } from '../../../modules/receivedResellerAuthorization/types'

export type RenewChannelOfferHeaderProps = {
  resellerAuthorization?: RenewChannelOfferResellerAuthorization
}

export type RenewChannelOfferFooterProps = {
  footerWidth: string
  handleSubmitChannelOffer: () => void
  saveChannelOffer: (args: {
    channelOffer: ChannelPartnerPrivateOfferInCreation
    saveAsDraft: boolean
  }) => void
}

export type RenewChannelOfferSubscriptionProps = {
  externalBaseURL?: string
}

export const RenewChannelOfferSubscription: React.FC<
  RenewChannelOfferSubscriptionProps
> = ({ externalBaseURL }) => {
  useSyncFlyoutRouteParams(!!externalBaseURL)

  const { classes } = useStyles()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const {
    cloud: routeCloud,
    accountId: routeAccountId,
    agreementOfferId,
    subscriptionId,
    authorizationId,
  } = useParams<{
    cloud: string
    accountId: string
    agreementOfferId?: string
    subscriptionId?: string
    authorizationId?: string
  }>()

  const fallbackAccountId = useAccountId({ product: 'flyout' }) || ''
  const accountId = routeAccountId || fallbackAccountId

  const activeReceivedResellerAuthorizations = useSelector<
    RenewChannelOfferResellerAuthorization[]
  >(
    (state: RootState) =>
      (state?.receivedResellerAuthorization
        ?.activeReceivedResellerAuthorizations ||
        []) as unknown as RenewChannelOfferResellerAuthorization[]
  )

  useEffect(() => {
    if (accountId && subscriptionId) {
      dispatch(getCurrentSubscriptionDetail(accountId, subscriptionId, 'AWS'))
    }
  }, [accountId, subscriptionId, dispatch])

  useEffect(() => {
    if (isEmpty(activeReceivedResellerAuthorizations) && accountId) {
      dispatch(getActiveReceivedResaleAuthorizations({ partnerId: accountId }))
    }
  }, [accountId, activeReceivedResellerAuthorizations, dispatch])

  useEffect(() => {
    return () => {
      dispatch(clearActiveReceivedResaleAuthorization())
    }
  }, [dispatch])

  const selectedReceivedResellerAuth =
    activeReceivedResellerAuthorizations?.find(
      item => item?.id === authorizationId
    )

  const cppoAgreementOffer: ChannelPartnerPrivateOfferInCreation = useSelector(
    state =>
      state?.subscriptions?.currentCPPOAgreementInCreation?.[subscriptionId!] ||
      null
  )

  const subscriptionDetail: SubscriptionDetailObject = useSelector(
    state =>
      state?.subscriptions?.currentSubscription?.[subscriptionId!] || null
  )

  useEffect(() => {
    if (
      selectedReceivedResellerAuth &&
      accountId &&
      subscriptionId &&
      !isEmpty(subscriptionDetail)
    ) {
      dispatch(
        getInProgressCPPOAgreementBasedOffer(
          accountId,
          subscriptionId,
          'AWS',
          agreementOfferId,
          selectedReceivedResellerAuth?.id
        )
      )
    }
  }, [
    selectedReceivedResellerAuth,
    subscriptionId,
    accountId,
    agreementOfferId,
    subscriptionDetail,
    dispatch,
  ])

  const footerWidth = externalBaseURL ? '100%' : 'calc(100% - 72px)'

  const { timeZone } = useTimeZone()

  const validationSchema = generateABOChannelOfferValidationSchema(
    timeZone || 'UTC',
    (selectedReceivedResellerAuth?.productType as PlanType) ||
      PlanType.SAAS_SUBSCRIPTION,
    selectedReceivedResellerAuth?.resellerLicenseAgreementFiles as
      | string[]
      | undefined,
    selectedReceivedResellerAuth?.discountType as DiscountType | undefined,
    selectedReceivedResellerAuth as ReceivedResellerAuthorization | undefined
  )

  const handleNext = (id: string, draft?: boolean) => {
    const cloudPath = routeCloud?.toLowerCase()
    if (!cloudPath || !accountId) {
      const fallbackRoute = draft
        ? `/flyout/subscriptions/${subscriptionId}/authorization/${authorizationId}/edit/${id}`
        : `/flyout/authorization/${selectedReceivedResellerAuth?.id}/channel-offers/${id}/${draft ? 'edit' : 'detail'}`
      navigate(fallbackRoute)
      return
    }
    if (!draft) {
      navigate(
        `/flyout/${cloudPath}/${accountId}/authorization/${
          selectedReceivedResellerAuth?.id
        }/channel-offers/${id}/${draft ? 'edit' : 'detail'}`
      )
    } else {
      navigate(
        `/flyout/${cloudPath}/${accountId}/subscriptions/${subscriptionId}/authorization/${authorizationId}/edit/${id}`
      )
    }
  }

  const saveChannelOffer = ({
    channelOffer,
    saveAsDraft,
  }: {
    channelOffer: ChannelPartnerPrivateOfferInCreation
    saveAsDraft: boolean
  }) => {
    if (selectedReceivedResellerAuth?.id && accountId) {
      dispatch(
        sendChannelOffer({
          channelOffer,
          saveAsDraft,
          partnerId: accountId,
          resellerAuthId: selectedReceivedResellerAuth.id,
          channelOfferId: agreementOfferId,
          handleNext,
        })
      )
    }
  }

  return (
    <>
      {!isEmpty(cppoAgreementOffer) &&
        !isEmpty(selectedReceivedResellerAuth) && (
          <Formik
            initialValues={cppoAgreementOffer}
            validationSchema={validationSchema}
            validateOnMount={true}
            onSubmit={values => {
              const isApplied =
                isNonEmptyNumber(values?.adjustmentPercentageInputValue) &&
                values?.adjustmentPercentageInputValue ===
                  values?.adjustmentPercentage
              if (isApplied) {
                saveChannelOffer({ channelOffer: values, saveAsDraft: false })
              }
            }}
          >
            {formikProps => {
              const handleSubmitChannelOffer = useCallback(async () => {
                await formikProps.submitForm()
              }, [formikProps])

              return (
                <Form>
                  <ScrollToFieldError />
                  <div
                    className={classes.root}
                    data-testid="channel-offer-formik-container"
                  >
                    <Grid container spacing={4}>
                      <Grid
                        size={{
                          sm: 12,
                          md: 8,
                          lg: 8,
                        }}
                      >
                        <AddEditChannelOfferHeader
                          resellerAuthorization={
                            selectedReceivedResellerAuth as never
                          }
                        />
                        <AddEditRenewChannelOfferForm
                          resellerAuthorization={selectedReceivedResellerAuth}
                          subscriptionDetail={subscriptionDetail}
                        />
                      </Grid>
                      <Grid
                        className={classes.rightContainer}
                        size={{
                          sm: 12,
                          md: 4,
                          lg: 4,
                        }}
                      >
                        <OfferSubscriptionDetail />
                        <AuthorizationDetails
                          resellerAuthorization={
                            selectedReceivedResellerAuth as never
                          }
                        />
                      </Grid>
                    </Grid>
                  </div>
                  <AddEditChannelOfferFooter
                    footerWidth={footerWidth}
                    handleSubmitChannelOffer={handleSubmitChannelOffer}
                    saveChannelOffer={saveChannelOffer}
                  />
                </Form>
              )
            }}
          </Formik>
        )}
    </>
  )
}
